import { Cookies } from 'react-cookie-consent';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CookieOutlined, PersonOutline } from '@mui/icons-material';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { useAuth } from '../context/AuthContext';
import LoginForm from '../forms/LoginForm';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import { useConfig } from '../utils/useConfig';
import Seo from '../components/seo/Seo';

function Login() {
  const { user } = useAuth();
  const config = useConfig();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (user) {
      navigate(from);
    }
  }, [user, navigate, from]);

  const cookieConsent = Cookies.get('cookieconsent_essential');

  // Obsługa pomyślnego logowania przez OAuth
  const handleOAuthSuccess = () => {
    setTimeout(() => {
      navigate(from);
    }, 500);
  };

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <>
      <motion.div
        className="min-h-[75vh] w-full flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="w-full max-w-md" variants={itemVariants}>
          <div className="text-center mb-8">
            <motion.div
              className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30,
                delay: 0.3,
              }}
            >
              <PersonOutline className="h-8 w-8 text-primary" />
            </motion.div>

            <motion.h1
              className="text-3xl font-bold text-gray-900"
              variants={itemVariants}
            >
              Zaloguj się do swojego konta
            </motion.h1>

            <motion.p className="mt-2 text-gray-600" variants={itemVariants}>
              Wpisz swoje dane, aby kontynuować
            </motion.p>
          </div>

          {cookieConsent !== 'true' && (
            <motion.div
              className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3 shadow-sm"
              variants={itemVariants}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <CookieOutlined className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-amber-800 font-medium mb-1 text-sm">
                  Wymagana zgoda na cookies
                </p>
                <p className="text-amber-700 text-sm">
                  Aby się zalogować, musisz zaakceptować pliki cookie. Po
                  akceptacji odśwież stronę.
                </p>
              </div>
            </motion.div>
          )}

          <motion.div
            className="bg-white py-8 px-6 shadow-xl sm:rounded-lg sm:px-10 border border-gray-100"
            variants={itemVariants}
            whileHover={{
              boxShadow:
                '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
            }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <LoginForm from={from} />
            </div>

            <motion.div
              className="mt-6 pt-6 border-t border-gray-200"
              variants={itemVariants}
            >
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    Lub kontynuuj z
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <GoogleOAuthProvider
                  clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}
                >
                  <GoogleLoginButton onSuccess={handleOAuthSuccess} />
                </GoogleOAuthProvider>
              </div>
            </motion.div>
          </motion.div>

          <motion.p
            className="mt-6 text-center text-sm text-gray-500"
            variants={itemVariants}
          >
            Potrzebujesz pomocy?
            <motion.a
              href="/kontakt"
              className="ml-1 font-medium text-primary hover:text-primary-dark"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Skontaktuj się z nami
            </motion.a>
          </motion.p>
        </motion.div>
      </motion.div>
      <Seo
        title={`Logowanie`}
        description={config.company.about.shortInfo || ''}
        image={config.company.logo}
        noindex={true}
      ></Seo>
    </>
  );
}

export default Login;
